import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractError } from "@/lib/api";
import { FAMILY, NAV } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Baby, Home, Phone, PawPrint, AlertTriangle, Plus, Pencil,
  Search, CalendarClock, MessagesSquare, FileText
} from "lucide-react";
import FamilyProfileCompletion from "@/components/FamilyProfileCompletion";

export default function FamilyDashboard() {
  const { user } = useAuth();
  const [family, setFamily] = useState(null);
  const [children, setChildren] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    try {
      const [f, c, b] = await Promise.all([
        api.get("/families/me"),
        api.get("/children"),
        api.get("/bookings"),
      ]);
      setFamily(f.data);
      setChildren(c.data);
      setBookings(b.data.results);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const profileComplete = family && family.address && family.emergency_contacts?.length > 0;
  const activeBookings = bookings.filter((b) => ["requested", "accepted", "paid"].includes(b.status));

  return (
    <div className="min-h-screen bg-stone2-50" data-testid={FAMILY.dashboard}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="section-eyebrow">Family dashboard</span>
            <h1 className="mt-2 text-3xl lg:text-4xl font-heading font-bold text-sage-900">
              Welcome home, {user?.name?.split(" ")[0]}.
            </h1>
            <p className="text-stone2-600 mt-1">Care that already knows your family.</p>
          </div>
          <Link to="/search" className="btn-primary" data-testid="family-find-sitters-btn">
            <Search className="w-4 h-4" /> Find sitters
          </Link>
        </div>

        <FamilyProfileCompletion family={family} children={children} />

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          {/* Household card */}
          <section className="paper-card p-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Home className="w-4 h-4 text-sage-600" />
                <h2 className="font-heading font-semibold text-sage-900 text-lg">Household</h2>
              </div>
              <Link to="/family/profile" data-testid={FAMILY.editFamily} className="text-sm text-sage-700 hover:text-sage-900 flex items-center gap-1">
                <Pencil className="w-3.5 h-3.5" /> Edit
              </Link>
            </div>
            <dl className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
              <div><dt className="field-label">Family name</dt><dd className="text-sage-900">{family?.family_name || "—"}</dd></div>
              <div><dt className="field-label">Address</dt><dd className="text-sage-900">{family?.address || "—"}, {family?.city} {family?.state} {family?.zip_code}</dd></div>
              <div><dt className="field-label">Preferred communication</dt><dd className="text-sage-900 capitalize">{family?.preferred_communication || "—"}</dd></div>
              <div><dt className="field-label">Parent phone</dt><dd className="text-sage-900">{family?.parent_phone || "—"}</dd></div>
              <div className="sm:col-span-2"><dt className="field-label">Home instructions</dt><dd className="text-stone2-700 leading-relaxed">{family?.home_instructions || "—"}</dd></div>
              <div className="sm:col-span-2"><dt className="field-label">Household rules</dt><dd className="text-stone2-700 leading-relaxed">{family?.household_rules || "—"}</dd></div>
            </dl>

            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              <div className="rounded-lg bg-sage-50 p-4">
                <div className="flex items-center gap-2 text-sage-800 font-semibold text-sm"><Phone className="w-3.5 h-3.5" /> Emergency contacts</div>
                {family?.emergency_contacts?.length ? (
                  <ul className="mt-2 space-y-2">
                    {family.emergency_contacts.map((c, i) => (
                      <li key={`${c.phone || ""}-${c.name || ""}-${i}`} className="text-sm text-sage-900">
                        <span className="font-semibold">{c.name}</span> · {c.relationship} · {c.phone}
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-stone2-500 mt-2">No emergency contacts yet.</p>}
              </div>
              <div className="rounded-lg bg-ochre-50 p-4">
                <div className="flex items-center gap-2 text-ochre-800 font-semibold text-sm"><PawPrint className="w-3.5 h-3.5" /> Pets</div>
                {family?.pets?.length ? (
                  <ul className="mt-2 space-y-1">
                    {family.pets.map((p, i) => (
                      <li key={`${p.name || ""}-${p.type || ""}-${i}`} className="text-sm text-ochre-900">
                        <span className="font-semibold">{p.name}</span> ({p.type}){p.notes ? ` — ${p.notes}` : ""}
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-stone2-500 mt-2">No pets listed.</p>}
              </div>
            </div>
          </section>

          {/* Quick actions */}
          <aside className="paper-card p-6 flex flex-col gap-4">
            <div>
              <span className="section-eyebrow">Quick actions</span>
              <h2 className="mt-2 font-heading font-semibold text-sage-900 text-lg">Get help fast</h2>
            </div>
            <Link to="/search" className="btn-primary justify-center"><Search className="w-4 h-4" /> Find a sitter</Link>
            <Link to="/family/listings" className="btn-outline-sage justify-center" data-testid="dashboard-my-listings"><FileText className="w-4 h-4" /> My listings</Link>
            <Link to="/bookings" className="btn-outline-sage justify-center"><CalendarClock className="w-4 h-4" /> View bookings</Link>
            <Link to="/messages" className="btn-secondary justify-center"><MessagesSquare className="w-4 h-4" /> Messages</Link>
            <Link to="/family/invoices" className="btn-outline-sage justify-center" data-testid="family-invoices-btn"><FileText className="w-4 h-4" /> Invoices</Link>
            <div className="mt-2 rounded-lg bg-sage-50 p-4">
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-sage-700">Active bookings</div>
              <div className="text-3xl font-heading font-bold text-sage-900 mt-1" data-testid="family-active-bookings-count">{activeBookings.length}</div>
            </div>
          </aside>
        </div>

        {/* Children */}
        <section className="mt-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="section-eyebrow">Children profiles</span>
              <h2 className="mt-2 font-heading font-semibold text-sage-900 text-2xl">Your children</h2>
            </div>
            <Link to="/family/children/new" data-testid={FAMILY.addChild} className="btn-accent">
              <Plus className="w-4 h-4" /> Add child
            </Link>
          </div>

          {children.length === 0 ? (
            <div className="mt-4 paper-card p-8 text-center">
              <Baby className="w-8 h-8 mx-auto text-sage-500" />
              <p className="mt-3 text-stone2-600">No children added yet. Add a profile so sitters arrive knowing exactly what your child needs.</p>
              <Link to="/family/children/new" className="btn-primary mt-4 inline-flex">Add first child</Link>
            </div>
          ) : (
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {children.map((c) => (
                <div key={c.id} className="paper-card p-5 hover:-translate-y-1 transition-transform duration-200" data-testid={`family-child-card-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-lg">
                      {c.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-sage-900">{c.name}</div>
                      <div className="text-xs text-stone2-500">Age {c.age}{c.diagnosis ? ` · ${c.diagnosis}` : ""}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(c.communication_style || []).slice(0, 3).map((s) => (
                      <span key={s} className="chip">{s}</span>
                    ))}
                    {c.elopement_risk && c.elopement_risk !== "none" && (
                      <span className="chip-ochre">Elopement: {c.elopement_risk}</span>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-stone2-600 line-clamp-2">{c.calming_strategies || c.interests || "—"}</p>
                  <div className="mt-4 flex gap-2">
                    <Link to={`/family/children/${c.id}`} data-testid={FAMILY.editChild} className="btn-outline-sage text-xs">Edit</Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
